import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { tbilisiMonthDay } from '@beautyapp/core';

/**
 * Quiet "birthday today" marker for the three visit surfaces that already
 * carry a customer's `birthDate` (user-card, the v2 user-selection modal,
 * add-appointment). Display only — no sending, no "in N days" count; that
 * belongs to `/marketing/birthdays` (`BirthdayGreetingService`), and
 * duplicating its same-day rule here would be a second place to get it wrong.
 *
 * A SOLID violet with white text, matching the weight of the money chips
 * beside it in the visit card — those use `[nzColor]="'#cf0000'"` and friends,
 * so a pale outline tag reads as a washed-out version of them rather than as
 * its own thing.
 *
 * Violet because every neighbouring colour is taken and means something else:
 * red is debt, green is balance, blue is minutes, yellow is cashback, and pink
 * is this app's alert colour (it themes `ant-alert-info` at rgb(255,240,243),
 * indistinguishable from `ant-alert-error`'s rgb(255,242,240) — so pink and red
 * both read as failures here, and a birthday is not one).
 *
 * The gift is an `nz-icon`, matching every other icon on this card. That is
 * safe here because the app registers no restricted NZ_ICONS set, so ng-zorro
 * loads glyphs dynamically — the same path `eye`, `swap`, `comment` and
 * `global` already take on this surface. It carries `aria-hidden` because the
 * text beside it already says what this is.
 *
 * All date arithmetic stays in `@beautyapp/core` (`monthDayOf` /
 * `tbilisiMonthDay`) — this component only compares the two `MM-DD` strings.
 */
@Component({
  selector: 'beautyapp-birthday-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, NzIconModule],
  template: `
    @if (isBirthdayToday()) {
      <span class="birthday-badge" data-test="birthday-badge">
        <span nz-icon nzType="gift" nzTheme="outline" aria-hidden="true"></span>
        {{ 'calendar.form.birthdayToday' | translate }}
      </span>
    }
  `,
  styles: [
    `
      /* Tinted, not filled. The card around it is now near-monochrome, so a
         solid block would be the loudest thing on a quiet surface — which is
         the problem this whole pass exists to remove. Violet-700 on violet-50
         measures 8.6:1, and it is the only hue here besides a debt. */
      .birthday-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        padding: 2px 10px;
        border-radius: 999px;
        background: #f5f3ff;
        color: #6d28d9;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
      }
    `,
  ],
})
export class BirthdayBadgeComponent {
  /**
   * A calendar date the SERVER computed — `YYYY-MM-DD` or `MM-DD`, from
   * `to_char` on the naive column.
   *
   * Deliberately not a `Date` and not an ISO instant. `users.birthDate` is
   * `timestamp without time zone`, and the API resolves it in its own
   * timezone before serialising: a birthday stored as `1987-09-02 00:00`
   * arrives as `1987-09-01T19:00:00.000Z`. Measured on stage. There is no way
   * to recover the intended day from that string on the client, so this input
   * refuses to accept one rather than render a marker that is a day out —
   * which, for a "today" badge, is the same as being wrong.
   *
   * A surface that only holds the raw instant should pass nothing and show no
   * badge until its endpoint supplies the computed day.
   */
  birthDate = input<string | null | undefined>(null);

  isBirthdayToday = computed(() => {
    const value = this.birthDate();
    if (!value) {
      return false;
    }

    // Accepts `YYYY-MM-DD` and `MM-DD`; anything longer is an instant, which
    // this component will not guess from.
    const monthDay =
      value.length === 5 ? value : value.length === 10 ? value.slice(5) : null;

    return monthDay !== null && monthDay === tbilisiMonthDay();
  });
}
